import React, { MouseEvent, ReactText, ReactNode, ReactElement } from 'react';
import { Placement } from '@popperjs/core';
import styled, { css } from 'styled-components';
import {
    ButtonGroup,
    Button,
    IconContainer,
    ButtonLabel
} from './HBButtonGroup.styles';
import HBTooltip from '../HBTooltip/HBTooltip';
import { vars } from '../../theme';
import { IPopoverProps } from '../HBPopover/HBPopover';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { MustIncludeOneOfBasicTestIdProps } from '../common/internal/test-id.types';
import { useGroupFocusRedirect } from '../common/internal/useGroupFocusRedirect';

export interface IOptionTooltip {
    /** Tooltip message content. */
    text: string | ReactNode;
    /** Forces the tooltip closed regardless of hover state. */
    forceClose?: boolean;
    /** Tooltip placement relative to the button; follows Popper.js placement values. */
    placement?: Placement;
    /** Inline styles applied to the tooltip content container. */
    customStyle?: React.CSSProperties;
    /** Inline styles applied to the tooltip's target wrapper element. */
    targetContainerStyle?: React.CSSProperties;
    /** Horizontal pixel offset from the default popper position. */
    offsetX?: IPopoverProps['offsetX'];
    /** Vertical pixel offset from the default popper position. */
    offsetY?: IPopoverProps['offsetY'];
    /** Keeps the tooltip visible while the user hovers over the tooltip content itself. */
    keepOpenOnHover?: boolean;
}
export interface HBButtonGroupOption {
    /** Unique identifier passed to `onChange` when this button is selected. */
    value: ReactText;
    /** Visible button label. */
    label: ReactNode;
    /** When true, the button is non-interactive and visually muted. */
    disabled?: boolean;
    /** Icon element rendered above (or beside) the label. */
    icon?: ReactElement;
    /** Tooltip shown on hover for this individual button. */
    tooltip?: IOptionTooltip;
    /** `data-testid` applied directly to this button. */
    testId?: string;
    /** Auto-prefixed test identifier for this button. */
    testIdName?: string;
}

export interface Props {
    /** Array of button options defining the group's items; each option can have its own icon, tooltip, and disabled state. */
    buttons: HBButtonGroupOption[];
    /** CSS width applied to the entire button group container. */
    width?: string;
    /** CSS height applied to each individual button in the group. */
    height?: string;
    /** Value of the currently selected button; matched against each option's `value` to determine active state. */
    selectedButton?: ReactText;
    /** Fires when a button is clicked, receiving the selected option's `value`. Use it to update `selectedButton`. */
    onChange(val: ReactText): void;
    /** CSS class forwarded to the group container element. */
    className?: string;
    /** When true, all buttons in the group are non-interactive and visually muted. */
    disabled?: boolean;
    /** When true, suppresses the visual highlight on the selected button. */
    ignoreActiveStyles?: boolean;
    /** Prevents button labels from wrapping to multiple lines. */
    isSingleLineText?: boolean;
    /** When true (default), stacks icon above label in column layout; when false, places icon beside label in a row. */
    setIconOnTopOfTheText?: boolean;
    /**
     * HTML id set on the `role="group"` container, so an `HBFieldControl` label
     * (`control.id`) can target it. When `aria-labelledby` is also set, focusing
     * the container moves focus to the selected button, or the first enabled
     * one. Without `aria-labelledby` (e.g. the legacy `HBButtonGroupField`) the
     * id is set and nothing else changes. Omitted: no id.
     */
    id?: string;
    /** Accessible name for the group when the field has no visible label (e.g. an `HBFieldControl` with `ariaLabel`). Ignored when `aria-labelledby` is set. Omitted: no attribute. */
    'aria-label'?: string;
    /** ID of the element that names the group (e.g. an `HBFieldControl` label). Omitted: the group is unnamed. */
    'aria-labelledby'?: string;
    /** Space-separated IDs of hint/error elements describing the group. Omitted: no attribute. */
    'aria-describedby'?: string;
    /**
     * Accepted so `HBFieldControl`'s bundle can be passed as-is, but not rendered:
     * ARIA does not support `aria-invalid` on `role="group"`. The error reaches
     * assistive tech through `aria-describedby`.
     */
    'aria-invalid'?: React.AriaAttributes['aria-invalid'];
}

const HBButtonGroup = ({
    testIdName,
    testId,
    buttons,
    onChange,
    width,
    height,
    selectedButton,
    className,
    disabled,
    setIconOnTopOfTheText = true,
    ignoreActiveStyles = false,
    isSingleLineText = false,
    id,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy
}: Props & MustIncludeOneOfBasicTestIdProps) => {
    const handleChange = (e: MouseEvent<HTMLButtonElement>) => {
        onChange(e.currentTarget.value);
    };

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName,
        suffix: 'button-group',
        componentName: 'HBButtonGroup',
        enforce: false
    });

    // Only an HBFieldControl-style label (aria-labelledby) needs focus sent to a
    // button. The legacy field wrappers also pass `id`, and must not change.
    const redirectsFocus = !!(id && ariaLabelledBy);

    const focusRedirectProps = useGroupFocusRedirect({
        enabled: redirectsFocus,
        getTarget: group => {
            const groupButtons = Array.from(
                group.querySelectorAll<HTMLButtonElement>('button')
            );
            return (
                groupButtons.find(
                    groupButton => groupButton.dataset.active === 'true'
                ) ?? groupButtons.find(groupButton => !groupButton.disabled)
            );
        }
    });

    return (
        <ButtonGroup
            role="group"
            id={id}
            {...focusRedirectProps}
            aria-label={ariaLabelledBy ? undefined : ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            width={width}
            height={height}
            className={className}
            data-testid={finalTestId}
            disabled={disabled}
        >
            {buttons.map(button => (
                <ButtonContent
                    key={button.value}
                    button={button}
                    handleChange={handleChange}
                    selectedButton={selectedButton}
                    setIconOnTopOfTheText={setIconOnTopOfTheText}
                    ignoreActiveStyles={ignoreActiveStyles}
                    isSingleLineText={isSingleLineText}
                />
            ))}
        </ButtonGroup>
    );
};

export default HBButtonGroup;

const ButtonContent = ({
    button,
    selectedButton,
    handleChange,
    setIconOnTopOfTheText,
    ignoreActiveStyles,
    isSingleLineText
}: {
    button: HBButtonGroupOption;
    selectedButton: ReactText;
    handleChange: (e: MouseEvent<HTMLButtonElement>) => void;
    setIconOnTopOfTheText: boolean;
    ignoreActiveStyles: boolean;
    isSingleLineText: boolean;
}) => {
    const finalTestId = useBuildTestIdInternal({
        testId: button.testId,
        identifier: button.testIdName,
        suffix: 'button-group-item',
        componentName: 'HBButtonGroup',
        enforce: false
    });

    const buttonDisabled = button.disabled;
    const commonButtonProps = {
        hasIcon: !!button.icon,
        disabled: button.tooltip ? false : buttonDisabled,
        key: button.value,
        active: button.value === selectedButton && !buttonDisabled,
        setIconOnTopOfTheText: setIconOnTopOfTheText,
        value: button.value,
        onClick: buttonDisabled ? () => {} : handleChange,
        type: 'button' as const,
        'data-active': button.value === selectedButton,
        'aria-pressed': button.value === selectedButton,
        'data-testid': finalTestId
    };

    const buttonContent = (
        <>
            {button.icon && (
                <IconContainer
                    active={button.value === selectedButton}
                    ignoreActiveStyles={ignoreActiveStyles}
                >
                    {button.icon}
                </IconContainer>
            )}
            <ButtonLabel isSingleLineText={isSingleLineText}>
                {button.label}
            </ButtonLabel>
        </>
    );

    return button.tooltip ? (
        <HBTooltip
            key={button.value}
            forceClose={!button.tooltip || button?.tooltip.forceClose}
            placement={button.tooltip?.placement}
            offsetX={button.tooltip?.offsetX}
            offsetY={button.tooltip?.offsetY}
            keepOpenOnHover={button.tooltip?.keepOpenOnHover}
            customStyle={button.tooltip?.customStyle}
            targetContainerStyle={button.tooltip?.targetContainerStyle}
            targetContainerClassName={
                button.value === selectedButton ? 'active' : ''
            }
            target={
                <ButtonWithTooltip
                    {...commonButtonProps}
                    disabledStyle={buttonDisabled}
                >
                    {buttonContent}
                </ButtonWithTooltip>
            }
        >
            {button.tooltip?.text}
        </HBTooltip>
    ) : (
        <Button {...commonButtonProps}>{buttonContent}</Button>
    );
};

const ButtonWithTooltip = styled(Button)<{
    disabledStyle: boolean;
}>`
    width: 100%;
    ${({ disabledStyle }) =>
        disabledStyle &&
        css`
            background-color: ${vars.colors.surface.disabled} !important;
            color: ${vars.colors.text.disabled} !important;
            cursor: not-allowed;
        `};
`;
